import { useApolloClient, useMutation } from '@apollo/client';
import { useNotify } from '@newfront-insurance/next-notifications';

import type { useSettingsAdmins } from './use-settings-admins';
import {
  GET_SETTINGS_USER_GROUPS,
  TRANSITION_SETTINGS_GROUP_USER_ROLE,
} from '../../../../queries/skill-administration';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { updateCachedGroupRoles } from '../../lib/skill-group-cache';
import { useSettingsRemoval } from '../settings-shared/use-settings-removal';

export function useAdminActions(organizationUuid: string, result: ReturnType<typeof useSettingsAdmins>) {
  const { settingsAdministration, userUuid: currentUserUuid } = useChatOverlayContext();
  const currentOrganization = settingsAdministration.organizations.find((root) => root.uuid === organizationUuid);
  const canManageAdmins = !!currentOrganization?.managers.includes(currentUserUuid);
  const client = useApolloClient();
  const notify = useNotify();
  const [transitionRole] = useMutation(TRANSITION_SETTINGS_GROUP_USER_ROLE, {
    errorPolicy: 'none',
    fetchPolicy: 'no-cache',
  });
  const removal = useSettingsRemoval(
    currentOrganization?.uuid ?? '',
    settingsAdministration.status === 'ready' && canManageAdmins && !result.loading && !result.error,
    currentUserUuid,
  );
  const revokeAdmin = (groupUuid: string, userUuid: string, name: string) => {
    if (!canManageAdmins || currentOrganization?.uuid !== groupUuid) return;
    return removal.run(
      userUuid,
      async () => {
        const { data } = await transitionRole({ variables: { groupId: groupUuid, userUuid, role: 'MEMBER' } });
        const group = data?.transitionSkillGroupUserRole;
        if (
          group?.uuid !== groupUuid ||
          group.parentGroupUuid !== null ||
          group.admins.includes(userUuid) ||
          !group.members.includes(userUuid)
        ) {
          throw new Error('The admin revocation was not confirmed. Try again.');
        }
        return group;
      },
      (group) => {
        result.updateAdmins(groupUuid, group.admins);
        updateCachedGroupRoles(client.cache, group);
        // Settings discovery is session-scoped and uncached. Refresh it to update parent-admin badges.
        void client
          .refetchQueries({ include: [GET_SETTINGS_USER_GROUPS] })
          .catch(() =>
            notify.error(
              'Admin access was revoked, but settings could not refresh. Reopen Settings to refresh access.',
            ),
          );
      },
      `${name}'s admin access revoked.`,
    );
  };
  const refreshAdmins = async () => {
    const refreshed = await client.refetchQueries({ include: [GET_SETTINGS_USER_GROUPS] });
    if (!refreshed.length) result.retry();
  };
  return { ...removal, revokeAdmin, refreshAdmins };
}
